import React, { FunctionComponent, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import {
  InsuredItem,
  SingleFieldStepProps,
  YesNoAnswer,
  YES_NO_OPTIONS,
} from 'scenes/common/types';
import { SegmentedControlQuestion } from 'scenes/common/components/Questions';
import { SlideIn, SubmitButton } from 'components';

export const BROKEN_IN_CLAIMS = 'brokenInClaims';
export const ADDITIONAL_SECURITY_INSTALLED = 'additionalSecurityInstalled';

const BreakInAndAdditionalSecurityStep: FunctionComponent<
  Pick<SingleFieldStepProps<string>, 'onNext'> & {
    brokenInClaimsDefault: YesNoAnswer;
    additionalSecurityInstalledDefault: YesNoAnswer;
    insuredItem: InsuredItem;
  }
> = ({
  brokenInClaimsDefault,
  additionalSecurityInstalledDefault,
  onNext,
  insuredItem,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    errors,
    control,
    watch,
    getValues,
  } = useForm({
    mode: 'onChange',
  });
  const watchedBrokenInClaims = watch(BROKEN_IN_CLAIMS, brokenInClaimsDefault);

  const onSubmit = useCallback(() => {
    onNext({
      brokenInClaims: watchedBrokenInClaims,
      additionalSecurityInstalled:
        getValues(ADDITIONAL_SECURITY_INSTALLED) ?? YesNoAnswer.Unspecified,
    });
  }, [getValues, onNext, watchedBrokenInClaims]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="on">
      <SegmentedControlQuestion
        options={YES_NO_OPTIONS}
        stateKey={BROKEN_IN_CLAIMS}
        question={`Did any of the claims relate to someone breaking into the ${insuredItem} you want to insure?`}
        defaultValue={brokenInClaimsDefault}
        control={control}
        errors={errors}
      />

      {watchedBrokenInClaims === YesNoAnswer.Yes && (
        <SlideIn>
          <SegmentedControlQuestion
            options={YES_NO_OPTIONS}
            stateKey={ADDITIONAL_SECURITY_INSTALLED}
            question={`Was additional security installed at this ${insuredItem} since the break in?`}
            defaultValue={additionalSecurityInstalledDefault}
            control={control}
            errors={errors}
          />
        </SlideIn>
      )}

      <SubmitButton enabled={hooksForm.isValid}>Next</SubmitButton>
    </form>
  );
};

export default BreakInAndAdditionalSecurityStep;
